import type { FileTotals, OfflineError, OfflineSceneState } from "@novorender/api";
import { createSlice, type Draft, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

type SyncedState = Extract<OfflineSceneState, { kind: "synced" }>;

/**
 * What novoweb knows a scene to be offline: the SDK's own data, which `offlineScreen.ts` turns
 * into the widget's screens. A durable state as `getState()` reads it; for a sync this tab runs,
 * its latest progress tick and then its outcome.
 */
export type OfflineSceneView =
    | Exclude<OfflineSceneState, { kind: "synced" }>
    /**
     * As `getState()` reads it. Right after this tab's sync completed, from its result, until the
     * state is read again: without the manifest id, which the result doesn't carry.
     */
    | (Omit<SyncedState, "manifestId"> & { readonly manifestId: string | undefined })
    /**
     * This tab's sync failed after it claimed the scene, so the directory reads `interrupted`. This
     * says why, until the scene is read again. A failure before the claim is the record's
     * `runFailure` instead. `downloaded` is unknown only for a failure the record couldn't place,
     * such as a superseded run's.
     */
    | { readonly kind: "failed"; readonly error: OfflineError; readonly downloaded: FileTotals | undefined }
    /** Enrolment found no offline manifest for the scene: it can't be had offline. */
    | { readonly kind: "unsupported" };

export type OfflineSceneRecord = {
    /** The scene's OPFS directory name. */
    id: string;
    view: OfflineSceneView;
    /**
     * The scene is syncing in another tab: it holds the sync lock, and only it can pause. Its
     * progress is the last it broadcast.
     */
    syncingElsewhere?: boolean;
    /** See `legacySyncDates`. */
    legacyLastSynced?: string;
    /**
     * The mini map bytes this tab's full sync covers, estimated before it began: `SyncProgress`
     * counts none. Unset when unknown, or for a sync without a mini map.
     */
    minimapBytes?: number;
    /**
     * The scene version the scene's committed host-data capture is of (`meta.manifestId`), `null`
     * if it has none novoweb reads. Unset until read, and never read where nothing is captured
     * (the Procore backend).
     */
    capturedManifestId?: string | null;
    /**
     * What the scene is called, as its state last said: carried across this tab's own
     * outcomes, whose views don't say. Unset for a scene never given a name, which is listed by
     * its id.
     */
    name?: string;
    /**
     * This tab's full sync failed before it claimed the scene. The SDK writes nothing until a sync
     * claims the scene, so `view` is as it was before the run. With nothing offline before, `view`
     * is `absent`. Cleared by the next run or the next state read.
     */
    runFailure?: OfflineError;
    /** This tab's delete of the scene failed: its files may still be on disk, all or some. */
    deleteFailure?: OfflineError;
};

/**
 * What `estimate()` said about the open scene, for the widget to show its download's size by. No
 * storage left is shown with it: the browser's own estimate of that is too far off to warn by, so a
 * download that doesn't fit fails as `quotaExceeded` instead.
 */
export type OfflineEstimate =
    | {
          kind: "ready";
          /** The scene's directory name: the estimate is of the open scene only. */
          sceneId: string;
          total: FileTotals;
      }
    | { kind: "failed"; sceneId: string; error: OfflineError };

/** Why the open scene couldn't be enrolled for incremental caching: its scene index couldn't be read. */
export type IncrementalFailure = {
    /** The scene's directory name. */
    sceneId: string;
    error: OfflineError;
};

const initialState = {
    scenes: {} as {
        [sceneDirName: string]: OfflineSceneRecord;
    },
    action: undefined as
        | undefined
        | {
              id?: string;
              action: "delete" | "deleteAll" | "fullSync" | "enrolIncremental" | "pause";
          },
    estimate: undefined as OfflineEstimate | undefined,
    incrementalFailure: undefined as IncrementalFailure | undefined,
    incrementalStorageSize: null as number | null,
    incrementalStorageLimit: null as number | null,
};

type State = typeof initialState;

/**
 * Whether a broadcast's totals for a scene should update its record. Only incremental records are
 * updated, as the eviction loop tracks no other kind. Every broadcast carries every scene and most
 * are unchanged, so skipping those keeps the widget from re-rendering on every pass.
 */
export function shouldUpdateIncrementalUsed(record: OfflineSceneRecord | undefined, used: FileTotals): boolean {
    return (
        record?.view.kind === "incremental" &&
        (record.view.used.bytes !== used.bytes || record.view.used.files !== used.files)
    );
}

export type OfflineActionKind = NonNullable<State["action"]>["action"];

export const offlineSlice = createSlice({
    name: "offline",
    initialState: initialState,
    reducers: {
        setAction: (state, action: PayloadAction<State["action"]>) => {
            state.action = action.payload;
        },
        addScene: (state, { payload: scene }: PayloadAction<OfflineSceneRecord>) => {
            // The SDK's data is readonly; the store never mutates it, only replaces it.
            state.scenes[scene.id] = scene as Draft<OfflineSceneRecord>;
        },
        updateScene: (
            state,
            { payload: { id, updates } }: PayloadAction<{ id: string; updates: Partial<OfflineSceneRecord> }>,
        ) => {
            const scene = state.scenes[id];
            if (scene) {
                state.scenes[id] = {
                    ...scene,
                    ...(updates as Draft<Partial<OfflineSceneRecord>>),
                };
            }
        },
        removeScene: (state, action: PayloadAction<string>) => {
            delete state.scenes[action.payload];
        },
        setEstimate: (state, action: PayloadAction<State["estimate"]>) => {
            state.estimate = action.payload;
        },
        setIncrementalFailure: (state, action: PayloadAction<State["incrementalFailure"]>) => {
            state.incrementalFailure = action.payload;
        },
        /** Applies one broadcast's totals in a single dispatch. */
        setIncrementalUsed: (state, { payload }: PayloadAction<{ id: string; used: FileTotals }[]>) => {
            for (const { id, used } of payload) {
                const scene = state.scenes[id];
                if (scene?.view.kind === "incremental" && shouldUpdateIncrementalUsed(scene, used)) {
                    state.scenes[id] = { ...scene, view: { ...scene.view, used } };
                }
            }
        },
        setIncrementalStorageSize: (state, action: PayloadAction<number>) => {
            state.incrementalStorageSize = action.payload;
        },
        setIncrementalStorageLimit: (state, action: PayloadAction<number>) => {
            state.incrementalStorageLimit = action.payload;
        },
    },
});

export const selectOfflineAction = (state: RootState) => state.offline.action;
export const selectOfflineScenes = (state: RootState) => state.offline.scenes;
export const selectOfflineEstimate = (state: RootState) => state.offline.estimate;
export const selectIncrementalFailure = (state: RootState) => state.offline.incrementalFailure;
export const selectIncrementalStorageSize = (state: RootState) => state.offline.incrementalStorageSize;
export const selectIncrementalStorageLimit = (state: RootState) => state.offline.incrementalStorageLimit;

const { actions, reducer } = offlineSlice;
export { actions as offlineActions, reducer as offlineReducer };
